/* =========================================================
   SENPAI DRIP — Shared product card behaviour
   Reveal, cursor tilt + glare, mobile swipe, autoplay,
   colour swatches that follow the photo on screen.
   Initialises every .sd-cards wrapper; safe to include twice.

   Shared observers: a 24-card collection page was building 24
   IntersectionObservers and binding 24 window resize listeners.
   One of each now serves every card. Behaviour is unchanged.

   Jumps of more than one photo (dot taps, colour taps, the
   autoplay wrap back to the first photo) turn snapping off for
   the length of the scroll. With scroll-snap-stop:always, iPhone
   Safari otherwise stops a long jump part-way and the card
   looks stuck.

   CHANGED: slides are positioned with the track's exact width
   (fractions kept) and every scroll is settled onto the nearest
   photo once it stops, so no sliver of the next or previous
   photo is ever left showing.

   Oct 2026: data-sd-instant="N" on a .sd-cards wrapper reveals
   its first N cards on load instead of waiting for a scroll.
   The rest still reveal as they come into view.
   ========================================================= */
(function(){
  'use strict';
  if (window.SDCards) return;

  var reduced  = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var fine     = window.matchMedia('(hover:hover) and (pointer:fine)');
  var mqMobile = window.matchMedia('(max-width:800px)');
  var STEP     = 70;   /* ms between cards entering together */
  var MAX_TILT = 4;    /* degrees */
  var SWAP_MIN = 180;  /* ms the old image takes to blur out before the new one lands */
  var JUMP_MAX = 900;  /* ms before snapping is restored even if scrollend never fires */
  var SETTLE   = 160;  /* CHANGED: ms of scroll silence before the track is squared up */
  var players  = [];   /* autoplay controllers */
  var syncers  = [];   /* dot-sync callbacks, run together on resize */

  /* lets :active fire on iOS for the tap press */
  document.addEventListener('touchstart', function(){}, { passive: true });

  /* ---------- reveal ---------- */
  var io = null;
  if (!reduced && 'IntersectionObserver' in window){
    io = new IntersectionObserver(function(entries){
      var batch = entries.filter(function(en){ return en.isIntersecting; });
      if (!batch.length) return;

      batch.sort(function(a, b){
        var ra = a.boundingClientRect, rb = b.boundingClientRect;
        if (Math.abs(ra.top - rb.top) > 8) return ra.top - rb.top;
        return ra.left - rb.left;
      });

      batch.forEach(function(en, i){
        var el = en.target;
        el.style.setProperty('--sd-delay', (i * STEP) + 'ms');
        el.classList.add('is-in');
        io.unobserve(el);
        setTimeout(function(){
          el.style.removeProperty('--sd-delay');
          el.classList.add('is-settled');
        }, 1500 + i * STEP);
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -10% 0px' });
  }

  /* ---------- reveal on load ----------
     The first few cards don't wait to be scrolled to: they play their
     entrance straight away in a quick stagger, so the ones just under
     the fold are already there. Their photo frame is also marked
     visible for sd-motion's image reveal, which then leaves it alone
     (it skips anything that already has .sd-vis). Two frames between
     is-ready and is-in so the entrance actually plays. */
  function showNow(el, i){
    var media = el.querySelector('.sd-card__media');
    if (media) media.classList.add('sd-vis');
    el.style.setProperty('--sd-delay', (i * STEP) + 'ms');
    requestAnimationFrame(function(){
      requestAnimationFrame(function(){
        el.classList.add('is-in');
        setTimeout(function(){
          el.style.removeProperty('--sd-delay');
          el.classList.add('is-settled');
        }, 1500 + i * STEP);
      });
    });
  }

  /* ---------- one visibility observer for every autoplaying card ---------- */
  var vio = null;
  function visibility(){
    if (vio || reduced || !('IntersectionObserver' in window)) return vio;
    vio = new IntersectionObserver(function(entries){
      entries.forEach(function(en){
        var ctl = en.target.sdPlayer;
        if (ctl) ctl.screen(en.isIntersecting);
      });
    }, { threshold: 0.55 });
    return vio;
  }

  /* ---------- one resize listener for every track ---------- */
  var rTimer = 0;
  window.addEventListener('resize', function(){
    if (rTimer) return;
    rTimer = setTimeout(function(){
      rTimer = 0;
      for (var i = 0; i < syncers.length; i++) syncers[i]();
    }, 150);
  }, { passive: true });

  /* ---------- tilt + glare ---------- */
  function tilt(card){
    var lift = card.querySelector('.sd-card__lift');
    if (!lift || reduced) return;

    var raf = 0, px = .5, py = .5;

    lift.addEventListener('pointermove', function(e){
      if (!fine.matches || e.pointerType !== 'mouse') return;
      var r = lift.getBoundingClientRect();
      px = Math.min(Math.max((e.clientX - r.left) / r.width, 0), 1);
      py = Math.min(Math.max((e.clientY - r.top) / r.height, 0), 1);
      card.classList.add('is-tilting');
      if (raf) return;
      raf = requestAnimationFrame(function(){
        raf = 0;
        lift.style.setProperty('--ry', ((px - .5) * 2 * MAX_TILT).toFixed(2) + 'deg');
        lift.style.setProperty('--rx', ((.5 - py) * 2 * MAX_TILT).toFixed(2) + 'deg');
        lift.style.setProperty('--gx', (px * 100).toFixed(1) + '%');
        lift.style.setProperty('--gy', (py * 100).toFixed(1) + '%');
      });
    });

    lift.addEventListener('pointerleave', function(){
      if (raf){ cancelAnimationFrame(raf); raf = 0; }
      card.classList.remove('is-tilting');
      /* spring settle comes from the CSS transition */
      lift.style.setProperty('--rx', '0deg');
      lift.style.setProperty('--ry', '0deg');
    });
  }

  /* ---------- mobile swipe + autoplay ---------- */
  function swipe(card, AUTO, SPEED, CYCLES){
    var track = card.querySelector('[data-sd-track]');
    var dots  = card.querySelectorAll('[data-sd-dot]');
    var media = card.querySelector('.sd-card__media');
    if (!track || !dots.length || !media) return;

    var ticking = false, swiped = false, startX = 0;
    var timer = null, userTook = false, onScreen = false;
    var jumpT = 0, jumpDone = null, lastSlide = -1;
    var W = 0, touching = false, settleT = 0;   /* CHANGED */

    /* CYCLES full loops, then the card rests on its first image.
       0 means keep looping, which holds the phone busy all visit. */
    var steps = 0;
    var limit = CYCLES > 0 ? CYCLES * dots.length : 0;

    /* CHANGED: slide width in layout pixels, fractions kept.
       clientWidth rounds (175.5 → 176), and that rounded number times
       the slide index is what left a sliver of the neighbouring photo
       showing. getComputedStyle also ignores the card's tilt/press
       transforms, which getBoundingClientRect would not. While the
       track is hidden (desktop) nothing is cached. */
    function measure(){
      var w = parseFloat(getComputedStyle(track).width) || track.clientWidth;
      W = w > 1 ? w : 0;
      return W || 1;
    }

    function index(){
      var i = Math.round(track.scrollLeft / (W || measure()));   /* CHANGED */
      return Math.min(Math.max(i, 0), dots.length - 1);
    }
    function sync(){
      var i = index();
      for (var n = 0; n < dots.length; n++){
        dots[n].classList.toggle('on', n === i);
      }
      /* tell the colour dots which photo is showing — but not mid-jump,
         or they'd flicker through every colour the scroll passes */
      if (!jumpDone && i !== lastSlide){
        lastSlide = i;
        if (card.sdOnSlide) card.sdOnSlide(i);
      }
    }

    /* CHANGED: once a scroll has fully stopped (finger up, momentum
       done, no jump running), square the track up on the nearest
       photo. iPhone Safari sometimes parks an auto-advance a few px
       short of the snap point and never corrects it — that was the
       strip of the previous photo on the left edge. */
    function settle(){
      settleT = 0;
      if (touching || jumpDone) return;
      measure();
      if (!W) return;
      var left = index() * W;
      if (Math.abs(track.scrollLeft - left) > .75){
        var sb = track.style.scrollBehavior;
        track.style.scrollBehavior = 'auto';
        track.scrollLeft = left;
        track.style.scrollBehavior = sb;
      }
    }
    function queueSettle(){
      clearTimeout(settleT);
      settleT = setTimeout(settle, SETTLE);
    }

    /* scroll to photo n. One step uses the normal snap scroll; anything
       longer turns snapping off until the scroll lands, then back on. */
    function jump(n){
      n = Math.min(Math.max(n, 0), dots.length - 1);
      var left = n * measure();   /* CHANGED: exact width, not rounded clientWidth */
      if (jumpDone) jumpDone();
      if (Math.abs(track.scrollLeft - left) < 2){ sync(); return; }

      if (reduced){
        track.scrollLeft = left;
        sync();
        return;
      }
      if (Math.abs(index() - n) <= 1){
        track.scrollTo({ left: left, behavior: 'smooth' });
        return;
      }

      track.style.scrollSnapType = 'none';
      function onEnd(){
        track.removeEventListener('scrollend', onEnd);
        if (jumpDone) jumpDone();
      }
      jumpDone = function(){
        jumpDone = null;
        clearTimeout(jumpT); jumpT = 0;
        track.removeEventListener('scrollend', onEnd);
        /* land exactly on the photo before snapping comes back */
        if (Math.abs(track.scrollLeft - left) > 2){
          var sb = track.style.scrollBehavior;
          track.style.scrollBehavior = 'auto';
          track.scrollLeft = left;
          track.style.scrollBehavior = sb;
        }
        track.style.scrollSnapType = '';
        sync();
        queueSettle();   /* CHANGED */
      };
      if ('onscrollend' in window) track.addEventListener('scrollend', onEnd);
      jumpT = setTimeout(function(){ if (jumpDone) jumpDone(); }, JUMP_MAX);
      track.scrollTo({ left: left, behavior: 'smooth' });
    }

    function advance(){
      jump((index() + 1) % dots.length);
      steps++;
      if (limit && steps >= limit){
        userTook = true;   /* same flag a real swipe sets — done for good */
        pause();
      }
    }
    function play(){
      if (timer || userTook || reduced || !AUTO) return;
      if (!mqMobile.matches || !onScreen || document.hidden) return;
      timer = setInterval(advance, SPEED);
    }
    function pause(){
      if (timer){ clearInterval(timer); timer = null; }
    }

    track.addEventListener('scroll', function(){
      queueSettle();   /* CHANGED: restarts on every scroll frame, fires once it goes quiet */
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function(){ ticking = false; sync(); });
    }, { passive: true });

    /* a swipe shouldn't open the product page, and it stops autoplay */
    track.addEventListener('touchstart', function(e){
      startX = e.touches[0].clientX;
      swiped = false;
      touching = true;           /* CHANGED */
      clearTimeout(settleT);     /* CHANGED: never fight a finger */
      pause();
      /* a finger on the track mid-jump: hand control back to snapping now */
      if (jumpDone){
        jumpDone = null;
        clearTimeout(jumpT); jumpT = 0;
        track.style.scrollSnapType = '';
      }
    }, { passive: true });

    track.addEventListener('touchmove', function(e){
      if (Math.abs(e.touches[0].clientX - startX) > 8){
        swiped = true;
        userTook = true;
      }
    }, { passive: true });

    /* CHANGED: finger up — let momentum finish, then square up */
    function release(){
      touching = false;
      queueSettle();
    }
    track.addEventListener('touchend', release, { passive: true });
    track.addEventListener('touchcancel', release, { passive: true });

    media.addEventListener('click', function(e){
      if (swiped){
        e.preventDefault();
        e.stopPropagation();
        swiped = false;
      }
    }, true);

    /* tapping a dot jumps to that image and takes over */
    Array.prototype.forEach.call(dots, function(dot, n){
      dot.addEventListener('click', function(e){
        e.preventDefault();
        e.stopPropagation();
        userTook = true;
        pause();
        jump(n);
      });
    });

    /* colour swatches drive the track through this — same hand-over as a dot tap */
    card.sdGoTo = function(n){
      userTook = true;
      pause();
      jump(n);
    };

    /* CHANGED: on resize, re-measure before syncing, then square up */
    syncers.push(function(){
      measure();
      sync();
      queueSettle();
    });

    if (AUTO && !reduced && 'IntersectionObserver' in window){
      var ctl = {
        play: play,
        pause: pause,
        screen: function(v){ onScreen = v; if (v) play(); else pause(); }
      };
      card.sdPlayer = ctl;
      players.push(ctl);
      visibility().observe(card);
    }
  }

  /* ---------- colour swatches ---------- */
  function swatches(card){
    var sws = card.querySelectorAll('[data-sd-sw]');
    if (!sws.length) return;

    var main   = card.querySelector('.sd-card__frame img:not(.sd-card__alt)');
    var altImg = card.querySelector('.sd-card__frame img.sd-card__alt');
    var slides = card.querySelectorAll('[data-sd-track] img');
    var first  = slides[0] || null;
    var links  = card.querySelectorAll('.sd-card__media, .sd-card__body');
    var qa     = card.querySelector('[data-sd-qa]');

    var bases = Array.prototype.map.call(links, function(a){
      return (a.getAttribute('href') || '').split('?')[0];
    });
    var firstOrig = first ? {
      src: first.getAttribute('src'),
      set: first.getAttribute('srcset'),
      col: first.getAttribute('data-sd-col') || ''
    } : null;

    function setVariant(vid){
      if (!vid) return;
      Array.prototype.forEach.call(links, function(a, n){
        a.setAttribute('href', bases[n] + '?variant=' + vid);
      });
      if (qa) qa.setAttribute('data-sd-qa-variant', vid);
    }

    /* light the dot for colour `col`. Colours hidden behind "+N" have no
       dot, so the current one stays lit rather than lighting the wrong one. */
    function light(col, pop){
      if (!col) return;
      var hit = null;
      for (var k = 0; k < sws.length; k++){
        if (sws[k].getAttribute('data-sd-sw-col') === col){ hit = sws[k]; break; }
      }
      if (!hit || hit.classList.contains('on')) return;
      for (var j = 0; j < sws.length; j++){
        sws[j].classList.toggle('on', sws[j] === hit);
      }
      setVariant(hit.getAttribute('data-sd-sw-variant'));
      if (pop && !reduced){
        hit.classList.remove('is-pop');
        void hit.offsetWidth;
        hit.classList.add('is-pop');
      }
    }
    Array.prototype.forEach.call(sws, function(sw){
      sw.addEventListener('animationend', function(){ sw.classList.remove('is-pop'); });
    });

    /* mobile: follow the swipe track (autoplay, swipes, dot taps) */
    card.sdOnSlide = function(i){
      if (!mqMobile.matches) return;
      var im = slides[i];
      if (im) light(im.getAttribute('data-sd-col'), true);
    };

    /* desktop: follow the hover photo */
    card.addEventListener('mouseenter', function(){
      if (!fine.matches || mqMobile.matches) return;
      if (altImg && !card.classList.contains('sd-sw-hold')) light(altImg.getAttribute('data-sd-col'), true);
    });
    card.addEventListener('mouseleave', function(){
      if (!fine.matches || mqMobile.matches) return;
      card.classList.remove('sd-sw-hold');
      if (main) light(main.getAttribute('data-sd-col'), true);
    });

    /* blur the old image out, preload the new one, blur it back in.
       img.sdTarget is where the image is HEADING, not what it shows now —
       so tapping back to the previous colour mid-fade is never ignored. */
    function swap(img, src, srcset){
      if (!img || !src) return;
      var heading = img.sdTarget || img.getAttribute('src');
      if (heading === src) return;
      img.sdTarget = src;

      function apply(){
        if (srcset) img.setAttribute('srcset', srcset);
        else img.removeAttribute('srcset');
        img.setAttribute('src', src);
      }

      var token = {};
      img.sdSwap = token;

      if (reduced){ apply(); img.classList.remove('sd-sw-out'); return; }

      img.classList.add('sd-sw-out');

      var t0 = Date.now(), done = false;
      function land(){
        if (done || img.sdSwap !== token) return;
        done = true;
        setTimeout(function(){
          if (img.sdSwap !== token) return;
          apply();
          requestAnimationFrame(function(){
            requestAnimationFrame(function(){
              if (img.sdSwap === token) img.classList.remove('sd-sw-out');
            });
          });
        }, Math.max(0, SWAP_MIN - (Date.now() - t0)));
      }

      var pre = new Image();
      pre.onload = pre.onerror = land;
      if (srcset){
        pre.sizes = img.getAttribute('sizes') || '';
        pre.srcset = srcset;
      }
      pre.src = src;
      setTimeout(land, 900);  /* slow network: don't leave the card blurred */
    }

    Array.prototype.forEach.call(sws, function(sw){
      sw.addEventListener('click', function(e){
        e.preventDefault();
        e.stopPropagation();

        for (var k = 0; k < sws.length; k++){
          sws[k].classList.toggle('on', sws[k] === sw);
        }

        var i   = parseInt(sw.getAttribute('data-sd-sw-i'), 10);
        var src = sw.getAttribute('data-sd-sw-src');
        var set = sw.getAttribute('data-sd-sw-srcset');
        var col = sw.getAttribute('data-sd-sw-col') || '';

        if (card.sdGoTo && mqMobile.matches){
          if (i >= 0){
            /* photo is already in the track — undo any earlier first-slide swap, then jump */
            if (firstOrig){
              swap(first, firstOrig.src, firstOrig.set);
              first.setAttribute('data-sd-col', firstOrig.col);
            }
            card.sdGoTo(i);
          } else if (src){
            /* photo isn't in the track — show it on the first slide */
            swap(first, src, set);
            first.setAttribute('data-sd-col', col);
            card.sdGoTo(0);
          }
        } else if (src){
          swap(main, src, set);
          if (main) main.setAttribute('data-sd-col', col);
          /* mouse still on the card: keep the hover photo out of the way */
          if (fine.matches && card.matches(':hover')) card.classList.add('sd-sw-hold');
        }

        setVariant(sw.getAttribute('data-sd-sw-variant'));
      });
    });

    /* start on the colour of the photo actually showing */
    if (card.sdGoTo && mqMobile.matches){
      if (first) light(first.getAttribute('data-sd-col'), false);
    } else if (main){
      light(main.getAttribute('data-sd-col'), false);
    }
  }

  document.addEventListener('visibilitychange', function(){
    players.forEach(function(p){ if (document.hidden) p.pause(); else p.play(); });
  });
  if (mqMobile.addEventListener){
    mqMobile.addEventListener('change', function(){
      players.forEach(function(p){ p.pause(); p.play(); });
    });
  }

  /* ---------- init ---------- */
  function init(root){
    if (!root || root.hasAttribute('data-sd-cards-ready')) return;
    root.setAttribute('data-sd-cards-ready', '');

    var AUTO    = root.getAttribute('data-sd-autoplay') === '1';
    var SPEED   = parseInt(root.getAttribute('data-sd-autoplay-speed'), 10) || 4000;
    var raw     = root.getAttribute('data-sd-autoplay-cycles');
    var CYCLES  = raw === null ? 2 : (parseInt(raw, 10) || 0);
    var INSTANT = parseInt(root.getAttribute('data-sd-instant'), 10) || 0;   /* cards revealed on load */
    var cards   = root.querySelectorAll('.sd-card');

    if (io) root.classList.add('is-ready');

    Array.prototype.forEach.call(cards, function(card, n){
      if (!io) card.classList.add('is-in', 'is-settled');
      else if (n < INSTANT) showNow(card, n);
      else io.observe(card);
      tilt(card);
      if (card.classList.contains('sd-card--multi')) swipe(card, AUTO, SPEED, CYCLES);
      swatches(card);   /* after swipe, so it can use card.sdGoTo */
    });
  }

  function initAll(scope){
    (scope || document).querySelectorAll('.sd-cards').forEach(init);
  }

  window.SDCards = { init: init, initAll: initAll };

  if (document.readyState === 'loading'){
    document.addEventListener('DOMContentLoaded', function(){ initAll(); });
  } else {
    initAll();
  }

  /* theme editor: re-init a section after it reloads */
  document.addEventListener('shopify:section:load', function(e){ initAll(e.target); });
})();